import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserOwnedCompanies, simulateCompanyMerge } from '../../../services/companyService';
import CompanySelector from './components/CompanySelector';
import ConflictPreview from './components/ConflictPreview';
import MergePreview from './components/MergePreview';
import ConfirmationModal from './components/ConfirmationModal';

const CompanyMergeSimulation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [ownedCompanies, setOwnedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  
  const [selectedCompanyA, setSelectedCompanyA] = useState(null);
  const [selectedCompanyB, setSelectedCompanyB] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Load owned companies
  useEffect(() => {
    const loadOwnedCompanies = async () => {
      setLoading(true);
      const result = await getUserOwnedCompanies(user?.id);
      
      if (result?.success) {
        setOwnedCompanies(result?.data || []);
      }
      
      setLoading(false);
    };

    if (user?.id) {
      loadOwnedCompanies();
    }
  }, [user?.id]);

  // Run simulation
  const handleRunSimulation = async () => {
    if (!selectedCompanyA || !selectedCompanyB) {
      return;
    }

    setSimulating(true);
    const result = await simulateCompanyMerge(selectedCompanyA?.id, selectedCompanyB?.id);
    
    if (result?.success) {
      setSimulationResult(result?.data);
      setCurrentStep(2);
    }
    
    setSimulating(false);
  };

  // Reset simulation
  const handleReset = () => {
    setCurrentStep(1);
    setSelectedCompanyA(null);
    setSelectedCompanyB(null);
    setSimulationResult(null);
  };

  const steps = [
    { number: 1, label: 'Company Selection', icon: 'Building2' },
    { number: 2, label: 'Conflict Analysis', icon: 'AlertTriangle' },
    { number: 3, label: 'Merge Preview', icon: 'Eye' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading companies...</p>
        </div>
      </div>
    );
  }

  if (ownedCompanies?.length < 2) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-border p-8 text-center">
          <div className="w-16 h-16 bg-error-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="#EF4444" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Insufficient Company Access
          </h2>
          <p className="text-text-secondary mb-6">
            You need ownership access to at least 2 companies to use the merge simulation feature.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-secondary-50 rounded-lg transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} color="#64748B" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-text-primary">
                  Company Merge Simulation
                </h1>
                <p className="text-sm text-text-secondary mt-1">
                  Preview and analyze company consolidation impact
                </p>
              </div>
            </div>
            
            {currentStep > 1 && (
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-primary hover:bg-primary-50 rounded-lg transition-colors duration-200 border border-primary flex items-center space-x-2"
              >
                <Icon name="RotateCcw" size={16} />
                <span>Start Over</span>
              </button>
            )}
          </div>
        </div>
      </header>
      {/* Progress Steps */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.number}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors duration-200 ${
                    currentStep >= step?.number
                      ? 'bg-primary text-white' :'bg-secondary-100 text-secondary-500'
                  }`}>
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={20} />
                    ) : (
                      <Icon name={step?.icon} size={20} />
                    )}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${
                      currentStep >= step?.number ? 'text-text-primary' : 'text-text-secondary'
                    }`}>
                      Step {step?.number}
                    </p>
                    <p className="text-xs text-text-secondary">{step?.label}</p>
                  </div>
                </div>
                {index < steps?.length - 1 && (
                  <div className="flex-1 h-0.5 mx-4 bg-secondary-200">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        currentStep > step?.number ? 'bg-primary' : 'bg-transparent'
                      }`}
                      style={{ width: currentStep > step?.number ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Warning Banner */}
        <div className="mb-6 bg-warning-50 border border-warning-200 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} color="#F59E0B" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-warning-800 mb-1">Simulation Mode Only</p>
            <p className="text-warning-700">
              This is a preview tool for analyzing merge impact. No actual changes will be made to your companies. 
              Real merging functionality requires backend implementation and proper data migration procedures.
            </p>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <CompanySelector
            companies={ownedCompanies}
            selectedCompanyA={selectedCompanyA}
            selectedCompanyB={selectedCompanyB}
            onSelectCompanyA={setSelectedCompanyA}
            onSelectCompanyB={setSelectedCompanyB}
            onNext={handleRunSimulation}
            simulating={simulating}
          />
        )}

        {currentStep === 2 && simulationResult && (
          <ConflictPreview
            result={simulationResult}
            onBack={() => setCurrentStep(1)}
            onNext={() => setCurrentStep(3)}
          />
        )}

        {currentStep === 3 && simulationResult && (
          <MergePreview
            result={simulationResult}
            onBack={() => setCurrentStep(2)}
            onConfirm={() => setShowConfirmModal(true)}
          />
        )}
      </div>
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmationModal
          result={simulationResult}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => {
            setShowConfirmModal(false);
            // In real implementation, this would trigger actual merge
            alert('This is a simulation only. No actual merge will be performed.');
          }}
        />
      )}
    </div>
  );
};

export default CompanyMergeSimulation;